/**
 * HVME Console - Shared Types
 *
 * WebSocket protocol types for the Agent Console.
 * These mirror the gateway WS message schema.
 */

// =============================================================================
// Privacy & Session
// =============================================================================

/** Privacy mode determines data retention and routing policy */
export type PrivacyMode = "standard" | "high-sensitivity" | "offline";

/** Memory retention policy for a session */
export interface MemoryPolicy {
  /** Default TTL for scratch memory in seconds */
  defaultTtlSeconds: number;
  /** Whether pinning is allowed in this privacy mode */
  allowPinning: boolean;
  /** Whether data can leave the device */
  allowRemoteStorage: boolean;
}

/** Map of privacy mode to default memory policy */
export const PRIVACY_DEFAULTS: Record<PrivacyMode, MemoryPolicy> = {
  standard: {
    defaultTtlSeconds: 3600,
    allowPinning: true,
    allowRemoteStorage: true,
  },
  "high-sensitivity": {
    defaultTtlSeconds: 300,
    allowPinning: false,
    allowRemoteStorage: false,
  },
  offline: {
    defaultTtlSeconds: 1800,
    allowPinning: true,
    allowRemoteStorage: false,
  },
};

/** Session state */
export interface Session {
  id: string;
  privacyMode: PrivacyMode;
  memoryPolicy: MemoryPolicy;
  startedAt: number;
  status: "initializing" | "ready" | "active" | "closing" | "closed";
}

// =============================================================================
// Agent Mesh
// =============================================================================

/** Agent role within the mesh */
export type AgentRole =
  | "orchestrator"
  | "executor"
  | "validator"
  | "observer";

/** Individual agent node in the constellation */
export interface AgentNode {
  id: string;
  role: AgentRole;
  label: string;
  status: "idle" | "working" | "complete" | "error";
  /** Position in the constellation (0-1 normalized) */
  x: number;
  y: number;
}

/** Connection thread between two agents */
export interface AgentThread {
  sourceId: string;
  targetId: string;
  status: "idle" | "active" | "complete";
  /** Data flow direction */
  direction: "forward" | "reverse" | "bidirectional";
}

// =============================================================================
// Tasks
// =============================================================================

/** Task lifecycle state */
export type TaskState =
  | "pending"
  | "routing"
  | "executing"
  | "validating"
  | "complete"
  | "failed";

/** A task submitted to the agent mesh */
export interface Task {
  id: string;
  description: string;
  state: TaskState;
  progress: number;
  assignedAgentId?: string;
  submittedAt: number;
  completedAt?: number;
  result?: string;
  error?: string;
}

// =============================================================================
// Memory
// =============================================================================

/** Location where memory item is stored */
export type MemoryLocation = "device" | "redis" | "pinned";

/** A scratch memory item with TTL */
export interface MemoryItem {
  id: string;
  key: string;
  preview: string;
  location: MemoryLocation;
  createdAt: number;
  ttlSeconds: number;
  /** Remaining seconds until expiry (computed client-side) */
  remainingSeconds: number;
  pinned: boolean;
  sizeBytes: number;
}

// =============================================================================
// WebSocket Protocol Messages
// =============================================================================

/** Wire message envelope - matches gateway protocol */
export interface WireMessage<T = unknown> {
  type: string;
  payload: T;
  sessionId?: string;
  requestId?: string;
}

/** Client -> Server messages (using gateway wire format) */
export type ClientMessage =
  | WireMessage<{ privacyMode?: PrivacyMode; label?: string; capabilities?: string[] }> & { type: "session.create" }
  | WireMessage<{ token: string }> & { type: "session.join" }
  | WireMessage<{ kind: string; encryptedData: string; priority?: number; timeoutSecs?: number }> & { type: "task.submit" }
  | WireMessage<{ key: string; reason?: string }> & { type: "mem.pin.request" }
  | WireMessage<{ key: string }> & { type: "mem.burn" }
  | WireMessage<Record<string, never>> & { type: "mem.burn-all" }
  | WireMessage<Record<string, never>> & { type: "session.close" };

/** Server -> Client messages */
export type ServerMessage =
  | { type: "session.ready"; session: Session }
  | { type: "presence.update"; agents: AgentNode[]; threads: AgentThread[] }
  | { type: "task.accepted"; task: Task }
  | { type: "task.progress"; taskId: string; progress: number; state: TaskState }
  | { type: "task.result"; taskId: string; result: string; state: "complete" }
  | { type: "task.error"; taskId: string; error: string; state: "failed" }
  | { type: "mem.update"; items: MemoryItem[] }
  | { type: "mem.ttl"; memoryId: string; remainingSeconds: number }
  | { type: "mem.expired"; memoryId: string }
  | { type: "error"; code: string; message: string };

// =============================================================================
// Connection
// =============================================================================

/** WebSocket connection status */
export type ConnectionStatus =
  | "disconnected"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error";

// =============================================================================
// Navigation
// =============================================================================

/** Console view/route identifiers */
export type ConsoleView =
  | "lobby"
  | "orchestration"
  | "memory"
  | "privacy";
